// The camera: turned freely under a pointer, resting on eighths (PLAN §5.7.2).
//
// **Orthographic by default, and that is still the design.** Every square the
// same size on screen is the entire reason a chessboard reads at a glance, and
// perspective taxes the exact mechanic this design worries about most (PLAN
// §7.1) — so perspective is a toggle a player asks for, never the default, and
// nothing in the game is allowed to depend on which one is up.
//
// **The two share one model**, which is what keeps that promise cheap: yaw,
// pitch, focus and `view` are the whole state, and the projection is a way of
// turning them into a matrix. The join is that a perspective camera stands
// back far enough that `view` world units fill the frame **at the focus
// plane** — so `viewHeight`, the pan scale, `screenHalf`, `fitView` and
// `covers` all keep meaning what they meant, exactly, for the plane the player
// is looking at, and approximately for the ground sloping away from it. Every
// place that approximation shows is named where it happens.

import { OrthographicCamera, PerspectiveCamera, Vector3 } from "three";

/**
 * Default pitch above the horizon. Two forces pull against each other and both
 * were measured on real renders rather than guessed:
 *
 *  - Flatter reads better. Below ~50 degrees you see piece *silhouettes* and
 *    can tell a queen from a bishop; at 58 everything is a disc seen from
 *    above, which is exactly the legibility failure PLAN §7.1 warns about.
 *  - Steeper occludes less. A piece of height h hides h*cos(pitch) of board
 *    behind it.
 *
 * Since it is a genuine trade rather than a right answer, the player can pick
 * the other end of it — `TILT_PITCH.top` — but not a point in between: a free
 * pitch drag is what left the board askew after every wobbly click.
 */
export const DEFAULT_PITCH = (44 * Math.PI) / 180;

/** How the board is looked at: across the table, or down onto it. */
export type Tilt = "table" | "top";

/**
 * The two angles the tilt control chooses between.
 *
 * Two presets rather than a continuous drag because pitch is a *reading* of
 * the board — silhouettes or a map — and not somewhere on the way to one.
 * `table` is the working angle; `top` is steep enough that a lane reads as a
 * route rather than as a row of parts, and short of straight down so pieces
 * are still objects standing on squares.
 */
export const TILT_PITCH: Record<Tilt, number> = {
  table: DEFAULT_PITCH,
  top: (70 * Math.PI) / 180
};

/**
 * Where the game opens: high, and an eighth of a turn round.
 *
 * Deliberately steeper than DEFAULT_PITCH. The trade above still holds — this
 * is nearer the angle where the set starts reading as discs — but the opening
 * position is the one moment where seeing the *board* matters more than
 * telling a queen from a bishop.
 *
 * PI/4 rather than a whole quarter turn: off-square, so the board reads as a
 * thing standing in space rather than a diagram squared up to the screen, and
 * both armies stay in frame. It is itself an eighth, so it is a resting place
 * `release` and `rotate` can both land on. Tilt or reset takes you back to the
 * flatter working angle.
 */
export const START_PITCH = (56 * Math.PI) / 180;
export const START_YAW = Math.PI / 4;

/**
 * How much board is on screen vertically, in squares. The board is 8 units,
 * so this is the classic board plus enough margin for the pieces' height.
 *
 * The *default*, not a constant, since the wheel moves it. Everything derived
 * from it — the pan scale, how far the view may travel, whether panning means
 * anything at all — reads the live value rather than this one.
 */
const VIEW_HEIGHT = 9.6;

/**
 * How far in the wheel may go: about three and a half squares tall, which is
 * a piece and its neighbours filling the screen. Closer than this and you can
 * no longer see what a piece is next to, which is most of what this game is.
 *
 * There is no matching constant for how far *out* — that is the board's
 * business rather than a number (`maxView`). Zooming out past the last square
 * shrinks the game to a speck in the middle of an empty screen, and a control
 * whose useful range is the first tenth of its travel reads as broken.
 */
/**
 * Breathing room around a fitted board, as a multiple.
 *
 * A board pressed exactly to the edges of the frame reads as cropped even
 * when every square is on screen. Named because `fitView` is also asked the
 * *bare* question — "is any of this off screen" — where the padding would be
 * a lie.
 */
const FIT_MARGIN = 1.06;

const MIN_VIEW = 3.4;
/** A backstop for a board that has grown beyond what is worth showing at once. */
const MAX_VIEW = 72;

const DISTANCE = 40;

/** Which way the world is projected. Orthographic is the design; see the top. */
export type Projection = "orthographic" | "perspective";

/**
 * The perspective lens, in radians. **A long lens on purpose.**
 *
 * This is the one number that decides how much the toggle costs. Wide is what
 * people mean by "3D" and it is precisely wrong here: at 60 degrees the
 * near rank is half again the size of the far one, so a square stops being a
 * unit of measure and the board stops reading at a glance — PLAN §7.1's
 * failure, arrived at deliberately. Thirty keeps enough convergence to say
 * "this is a space" and leaves a square recognisably a square across a grown
 * board.
 *
 * It also bounds every approximation in this file, all of which are exact at
 * the focus plane and drift with the tangent of half this angle.
 */
const PERSPECTIVE_FOV = (30 * Math.PI) / 180;

/** Near plane for the perspective camera. Far is derived per frame. */
const PERSPECTIVE_NEAR = 0.3;
/** How far past the focus the far plane reaches. A grown board is ~40 across. */
const PERSPECTIVE_FAR_PAD = 300;

const QUARTER = Math.PI / 2;
/**
 * Where a free turn is allowed to come to rest.
 *
 * Eight, not four: the diagonal views are where both armies and the whole of a
 * lane's run are in frame at once, and the game opens on one of them. Snapping
 * on release is what stops a board sitting a few degrees askew for the rest of
 * a game because a click wobbled.
 */
const EIGHTH = Math.PI / 4;

/**
 * How a fling slows, per second, as the rate of an exponential decay.
 *
 * The model every map uses: the release hands the hand's own speed to the
 * view and friction takes it back. A throw therefore covers `speed / this`,
 * so a fast flick — thirty units a second is an easy one — crosses seven or
 * eight squares and is over in a second and a half. It was 1.4, twenty squares
 * and four seconds, and that read as the board getting away from the hand:
 * the fling is there to finish a drag, not to cross a grown board on its own.
 * Clamped like any other pan, so it cannot throw the board off the edge.
 */
const FLING_FRICTION = 4;
/**
 * How much of a frame's measured speed the running estimate takes on.
 *
 * Pointer events and frames interleave unevenly, so one frame's delta over its
 * `dt` is noisy; half is enough smoothing that a pointer resting a few frames
 * before lifting throws nothing, which is the difference between a flick and
 * a drag that was already finished.
 */
const VELOCITY_SMOOTHING = 0.5;
/**
 * Below this, in world units per second, a pan is not moving.
 *
 * A tenth of a square a second is a pixel every few frames — and at
 * `FLING_FRICTION` it is a fortieth of a square of travel still owed, so
 * ending here truncates nothing anybody can see. It has to be a real number
 * rather than a hair above zero because the decay is exponential: a threshold
 * ten times smaller is another second and a half of the loop staying awake to
 * move the board a pixel.
 */
const STILL = 0.1;
/**
 * How fast a held pan key carries the view, in view heights per second.
 *
 * Screen-relative rather than in squares, so a key crosses the screen in the
 * same time however far the wheel has been turned — a fixed number of squares
 * is a crawl zoomed out and a bolt zoomed in. Just over one, so a press and a
 * release is most of a screen and there is no repeat rate in the loop.
 */
const KEY_SPEED = 1.5;

/**
 * How hard the camera follows its target, per second, as the rate of an
 * exponential approach.
 *
 * Two values because the two cases want different things: a turn under a
 * pointer has to feel attached to the hand, and easing to a stored viewpoint
 * wants to look like a considered move rather than a snap.
 *
 * **The focus takes neither while it is being driven.** A hand, a fling and a
 * held key write it directly (`glued`) — an ease behind a pointer is a lag,
 * and a lag is what the pan overhaul was called in to remove. What is left
 * here is the yaw, the pitch and everything the camera *arrives* at.
 */
const DRAG_FOLLOW = 32;
const EASE_FOLLOW = 9;
/**
 * Zoom follows at its own rate, rather than borrowing whichever of the two
 * above is in force.
 *
 * A wheel notch has no pointer to be attached to and no viewpoint to arrive
 * at: it is a discrete request that wants to be *finished*. At the easing rate
 * a flick of the wheel takes a third of a second to land, which reads as the
 * scroll not having registered — and the usual response to that is another
 * flick, so it overshoots twice as far as anyone asked for.
 */
const ZOOM_FOLLOW = 20;

export interface CameraPose {
  yaw: number;
  pitch: number;
  /** Board-plane point the camera looks at. The board grows; the view follows. */
  focusX: number;
  focusZ: number;
  /**
   * How far in the view is zoomed, as a view height.
   *
   * Optional because a pose is also written by hand for a side that has never
   * held the camera, and "wherever we are now" is a better default there than
   * any number this file could pick.
   */
  view?: number;
}

/**
 * How far past the board's edge the focus may go, as a fraction of the
 * visible half-screen's shorter side.
 *
 * In screen terms rather than squares, so it means the same thing at every
 * zoom: about three and a half squares at the default zoom, a square when
 * zoomed right in, nine on a zoomed-out camp. Enough to put any corner
 * mid-screen with room for its card, and short enough that the board is
 * always on screen — at the very corner of the limit, the nearest corner of
 * the board is still inside the view. Four squares was the old number, and it
 * was nothing once the board outgrew the screen and everything once it was
 * zoomed into.
 */
const EDGE_ROOM = 0.6;

/** How much further out than "everything fits" the wheel may go. */
const ZOOM_OUT_SLACK = 1.6;

export class CameraRig {
  /**
   * Both cameras exist for the life of the rig, and the toggle picks one.
   *
   * Built together rather than swapped in on demand: the scene, the raycaster
   * and every `project` call take whichever this returns, so a rig that had to
   * *construct* one would have a frame where `camera` is a camera nobody has
   * sized yet. Two objects is nothing; a half-built projection is a black
   * frame.
   */
  private readonly ortho: OrthographicCamera;
  private readonly persp: PerspectiveCamera;
  private mode: Projection = "orthographic";
  private yaw = START_YAW;
  private targetYaw = START_YAW;
  private pitch = START_PITCH;
  private targetPitch = START_PITCH;
  private focus = new Vector3(0, 0, 0);
  private targetFocus = new Vector3(0, 0, 0);
  /** True while a pointer is turning the camera: follow the hand hard. */
  private dragging = false;
  /** True while a pointer is panning: the focus is glued to it, not eased. */
  private panning = false;
  /**
   * Pan deltas since the last update, and the speed they imply.
   *
   * `x` is `right` and `z` is `forward`, the pair `pan` takes. `update` turns
   * the sweep into a smoothed speed while the pointer is down; `release`
   * leaves it standing as the fling's speed and `update` spends it against
   * friction. A turn clears both, because a released turn snaps rather than
   * flings.
   */
  private swept = new Vector3(0, 0, 0);
  private velocity = new Vector3(0, 0, 0);
  /**
   * Which way the held pan keys are pointing, screen-relative, each −1, 0 or 1.
   *
   * A direction rather than a step, because a key is *held*: the caller says
   * when it changes and `update` integrates it, so the glide is one motion at
   * a known speed instead of a stack of nudges arriving at the platform's
   * repeat rate.
   */
  private driving = { right: 0, forward: 0 };
  /**
   * Where the *player* last left the view.
   *
   * The director moves the camera on its own — to an opponent's move, to
   * ground that is about to open — and those looks are borrowed, not given.
   * Without somewhere to put the view back, a run slowly drags the board away
   * from wherever you were working and you spend the game re-finding your
   * factory. Only a deliberate move updates it: a drag, a pan, a centring.
   */
  private anchor = new Vector3(0, 0, 0);
  /** How much board is on screen vertically, and where the wheel is taking it. */
  private view = VIEW_HEIGHT;
  private targetView = VIEW_HEIGHT;
  /** Last value written into the projection, so it is rebuilt only on a change. */
  private appliedView = -1;
  private aspect = 1;
  /** Pixels of canvas covered by interface, per edge. See `setViewInset`. */
  private inset = { left: 0, right: 0, top: 0, bottom: 0 };
  private pixels = { width: 1, height: 1 };
  /**
   * The board's own rectangle in world units — every square that exists, not
   * where the view may go.
   *
   * Those were the same thing while the view was a fixed size, and the extent
   * was stored pre-shrunk. They stopped being the same thing the moment the
   * wheel could change how much fits on screen: how far you may pan is the
   * board *less what is already visible*, and the second half of that now
   * moves. Storing the board and deriving the rest is what keeps zooming in on
   * a small board from leaving you unable to reach the corner you zoomed past.
   */
  private extent = { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };

  constructor(aspect: number) {
    this.ortho = new OrthographicCamera(-1, 1, 1, -1, 0.1, 200);
    this.persp = new PerspectiveCamera(
      (PERSPECTIVE_FOV * 180) / Math.PI,
      1,
      PERSPECTIVE_NEAR,
      PERSPECTIVE_FAR_PAD
    );
    this.setAspect(aspect);
    this.apply();
  }

  /** The camera to render, raycast and project with. */
  get camera(): OrthographicCamera | PerspectiveCamera {
    return this.mode === "perspective" ? this.persp : this.ortho;
  }

  get projection(): Projection {
    return this.mode;
  }

  /**
   * Switch projections, keeping the view exactly where it is.
   *
   * Nothing about the *pose* moves — yaw, pitch, focus and `view` are shared,
   * and `view` means the same thing to both (see the top of the file), so the
   * board is the same size in the middle of the screen either way and the
   * switch reads as the edges converging rather than as the camera jumping.
   *
   * Snapped rather than eased. The two projections are not points on one scale
   * — there is no matrix half way between them that is either — so an ease
   * would have to cross-fade two renders, which is a lot of machinery to make
   * a settings toggle look like a camera move it is not.
   */
  setProjection(mode: Projection): void {
    if (mode === this.mode) return;
    this.mode = mode;
    // The other camera has never been sized, or was sized for a `view` this
    // rig has since left. `applyProjection` short-circuits on an unchanged
    // view, so it has to be told the one it is holding is not the one showing.
    this.appliedView = -1;
    this.clampFocus();
    this.apply();
  }

  /**
   * How far the camera stands from the focus.
   *
   * Fixed for the orthographic camera, where it decides nothing but the near
   * and far planes. **Derived for the perspective one**, and that derivation is
   * the whole join between the two: standing at `(view / 2) / tan(fov / 2)`
   * makes `view` world units exactly fill the frame's height at the focus
   * plane, which is what lets every screen-space number in this file go on
   * meaning what it meant.
   *
   * A function of `view` alone, deliberately — **not of the inset**, which is
   * absorbed by the field of view instead. If the inset moved the camera, then
   * `setViewInset` and `setPixelSize` would have to reposition it, and those
   * are called from resize handlers; `apply` would stop being the one writer.
   */
  private get distance(): number {
    if (this.mode !== "perspective") return DISTANCE;
    return this.view / 2 / Math.tan(PERSPECTIVE_FOV / 2);
  }

  setAspect(aspect: number): void {
    this.aspect = aspect;
    this.appliedView = -1;
    this.applyProjection();
  }

  /**
   * How much of the canvas the interface is sitting on, in pixels per edge.
   *
   * **The board is drawn behind the whole page, and only part of that page is
   * board you can reach.** A canvas-centred camera therefore puts the middle of
   * the board under the panel — and since the panel is opaque and takes clicks,
   * the pieces there are not merely hidden but *unusable*: measured, a2
   * projected to x=322 with the panel occupying 0–468, and clicking it selected
   * nothing at all.
   *
   * Done with a projection offset rather than by moving the focus, because the
   * offset is a property of the *view* and not of where you are looking: `fit`,
   * `centre` and every borrowed look keep working untouched, and — the reason
   * it is the right tool — `project` and `unproject` stay each other's inverse,
   * so picking, badges and grid labels all follow with no changes at all.
   *
   * Answers whether the free area actually moved, because the caller re-fits
   * on that and on nothing else — see `BoardRenderer.setViewInset`.
   */
  setViewInset(inset: { left: number; right: number; top: number; bottom: number }): boolean {
    const same =
      inset.left === this.inset.left &&
      inset.right === this.inset.right &&
      inset.top === this.inset.top &&
      inset.bottom === this.inset.bottom;
    if (same) return false;
    this.inset = { ...inset };
    this.appliedView = -1;
    this.applyProjection();
    return true;
  }

  /**
   * The aspect of the part of the canvas nothing is covering.
   *
   * `aspect` stays the canvas's, because that is what the *frustum* is built
   * from; this is what anything asking "how much board can I actually see"
   * wants, which is fitting, panning limits and `covers`.
   */
  private get freeAspect(): number {
    const { left, right, top, bottom } = this.inset;
    // Nothing covering it — including every test, which never sets a pixel
    // size, so this must not try to derive one.
    if (!left && !right && !top && !bottom) return this.aspect;
    const free = this.pixels.width - left - right;
    if (free <= 0) return this.aspect;
    // The projection keeps the *visible* height at `view`, so world units per
    // pixel is `view / height` and a width of `free` pixels is `free / height`
    // of a `view`. That is the same shape `aspect` has, which is what makes it
    // a drop-in for it.
    return free / this.pixels.height;
  }

  /** Canvas size in CSS pixels, so the inset can be turned into a frustum. */
  setPixelSize(width: number, height: number): void {
    if (width === this.pixels.width && height === this.pixels.height) return;
    this.pixels = { width: Math.max(1, width), height: Math.max(1, height) };
    this.appliedView = -1;
    this.applyProjection();
  }

  /**
   * Size the frustum from the current zoom and viewport shape.
   *
   * Separate from `apply`, which places the camera: the projection changes on
   * a resize and on a wheel notch, and the position changes every frame of
   * every motion. Guarded on the view actually having moved so a settled
   * camera does not rebuild a matrix sixty times a second for nothing.
   */
  private applyProjection(): void {
    if (this.view === this.appliedView) return;
    this.appliedView = this.view;

    const { width, height } = this.pixels;
    const { left, right, top, bottom } = this.inset;
    // The virtual image the frustum describes: the canvas plus the covered
    // strips, so that the sub-rectangle actually shown is the *free* area.
    const fullWidth = width + left + right;
    const fullHeight = height + top + bottom;

    // Sized so the **visible** height stays `view` whatever the inset is —
    // otherwise opening a panel would silently zoom the board out, and the
    // wheel would mean a different amount at every layout.
    //
    // The width comes from `aspect` scaled by the inset, **not** from the
    // pixel sizes: `pixels` is 1x1 until the first resize and in every test
    // that builds a rig directly, which made the frustum square and every
    // fitting question answer nonsense. `aspect` is always right.
    const halfHeight = (this.view * (fullHeight / height)) / 2;
    const halfWidth = (this.view * this.aspect * (fullWidth / width)) / 2;
    this.ortho.left = -halfWidth;
    this.ortho.right = halfWidth;
    this.ortho.top = halfHeight;
    this.ortho.bottom = -halfHeight;

    // **The same two numbers, said as an angle.** The perspective camera has
    // no frustum rectangle to write, so the height becomes a field of view at
    // the focus plane and the width becomes an aspect. Both are derived from
    // the halves above rather than from the pixel sizes, for the same reason
    // those are: `pixels` is 1x1 until the first resize and in every test that
    // builds a rig directly.
    //
    // This is also where the inset lands, instead of on the distance — see
    // `distance` for why that matters.
    this.persp.fov = (2 * Math.atan(halfHeight / this.distance) * 180) / Math.PI;
    this.persp.aspect = halfWidth / halfHeight;
    this.persp.near = PERSPECTIVE_NEAR;
    this.persp.far = this.distance + PERSPECTIVE_FAR_PAD;

    for (const camera of [this.ortho, this.persp]) {
      if (left || right || top || bottom) {
        // The offset is the *far* edge's inset, which is the half of this most
        // easily got backwards: to push content away from a panel on the left,
        // the canvas has to show the sub-rectangle at the virtual image's left
        // edge, so the image's middle lands right of the canvas's middle.
        camera.setViewOffset(fullWidth, fullHeight, right, bottom, width, height);
      } else {
        camera.clearViewOffset();
      }
      camera.updateProjectionMatrix();
    }
  }

  /**
   * The screen, in board-plane units: half a viewport across, and half a
   * viewport up the board.
   *
   * The second is divided by sin(pitch) for the same reason a vertical pan is:
   * the board is seen at an angle, so a screen of a given height covers more
   * *ground* than its own height. The floor matches `panCamera`'s, and stops a
   * near-flat camera claiming to see the whole world.
   */
  private get screenHalf(): { across: number; up: number } {
    return {
      // **The free area, not the canvas.** The view offset centres the board
      // in the part of the page the interface is not sitting on; this is the
      // other half of that, and without it `fit` still measures against the
      // whole window and pulls back just far enough to put the board's edges
      // underneath the panel. Falls back to the canvas when nothing is
      // covering it, which is every board with no panel and every test.
      across: (this.view * this.freeAspect) / 2,
      up: this.view / 2 / Math.max(0.25, Math.sin(this.pitch))
    };
  }

  /**
   * A board-plane offset, in screen axes: how far across the screen it lands
   * and how far up it.
   *
   * The pitch is left out on purpose — these are ground distances, and the
   * foreshortening that turns them into pixels is already in `screenHalf`.
   */
  private toScreen(dx: number, dz: number): { across: number; up: number } {
    const cos = Math.cos(this.yaw);
    const sin = Math.sin(this.yaw);
    return { across: dx * cos - dz * sin, up: dx * sin + dz * cos };
  }

  /** The inverse: back from screen axes to a board-plane offset. */
  private fromScreen(across: number, up: number): { dx: number; dz: number } {
    const cos = Math.cos(this.yaw);
    const sin = Math.sin(this.yaw);
    return { dx: across * cos + up * sin, dz: -across * sin + up * cos };
  }

  /**
   * Move the target focus by a screen-relative delta. True if it all landed.
   *
   * One door, because a hand, a fling and a held key are the same slide — three
   * copies of the basis change is three places for a sign to drift. The delta
   * is negated: pushing the board right is the camera looking left.
   *
   * **One origin, the target**, since the focus is glued to it through all
   * three: the hand takes the target over when it lands (`pan`) and `update`
   * writes the focus from it every frame after that, so there is no second
   * point to measure from.
   *
   * The answer is for the fling: a throw that has reached the edge has to stop
   * rather than push against the clamp for another second.
   */
  private slide(right: number, forward: number): boolean {
    const { dx, dz } = this.fromScreen(-right, -forward);
    const wantX = this.targetFocus.x + dx;
    const wantZ = this.targetFocus.z + dz;
    this.targetFocus.x = wantX;
    this.targetFocus.z = wantZ;
    // Strict: every caller is a request to look somewhere else, refused for
    // the same reason when there is nowhere else to look.
    this.clampFocus();
    // And every caller is the player saying where they want to be looking.
    this.anchor.copy(this.targetFocus);
    // Loose enough to survive the clamp's round trip through the screen basis,
    // which is exact in theory and a few ulps out in floating point.
    return (
      Math.abs(this.targetFocus.x - wantX) < 1e-9 &&
      Math.abs(this.targetFocus.z - wantZ) < 1e-9
    );
  }

  /**
   * Put down a fling, wherever it is in flight.
   *
   * One door for it, because every camera move that is not the fling *is* the
   * end of it: a view that goes on gliding after a look, a zoom or a fresh
   * grab is the board arguing with the player.
   */
  private stopFling(): void {
    this.velocity.set(0, 0, 0);
    this.swept.set(0, 0, 0);
  }

  /** True while a released throw is still carrying the view. */
  private get flinging(): boolean {
    return !this.panning && (this.velocity.x !== 0 || this.velocity.z !== 0);
  }

  /**
   * True while the focus is carried directly rather than eased toward.
   *
   * The three cases where the view is *being moved* rather than arriving
   * somewhere: a hand on the board, the throw it let go of, and a held key.
   * All three want the ground to go exactly as far as they ask, which an ease
   * cannot do — it lags by a frame's worth at best, and that lag is the whole
   * of what read as clunky.
   */
  private get glued(): boolean {
    return this.panning || this.flinging || !!this.driving.right || !!this.driving.forward;
  }

  /**
   * Pull the *target* back inside the limit. Never the live focus — see
   * `update`.
   *
   * **One limit for every caller, on the board's own axes**: the board's
   * rectangle grown by `EDGE_ROOM` of the visible half-screen on every side.
   * There used to be two, measured along the screen's axes — a pan took "how
   * much board is off screen", which stopped a corner at the screen's edge so
   * it could be looked at but never looked around, and collapsed to nothing
   * at the zoom where the board exactly fit; and a screen-axis box around a
   * board turned to the opening yaw is loose by the whole diamond at its
   * corners, so the focus could sit far off a diagonal edge over nothing. A
   * rectangle in board space is exactly "room past every edge", whichever way
   * the view is turned. Recomputed every call: the room depends on the zoom.
   */
  private clampFocus(): void {
    const screen = this.screenHalf;
    const room = Math.min(screen.across, screen.up) * EDGE_ROOM;
    this.targetFocus.x = Math.min(
      this.extent.maxX + room,
      Math.max(this.extent.minX - room, this.targetFocus.x)
    );
    this.targetFocus.z = Math.min(
      this.extent.maxZ + room,
      Math.max(this.extent.minZ - room, this.targetFocus.z)
    );
  }

  /**
   * Turn the board under a pointer drag. **Yaw only** — pitch is a pair of
   * presets now (`setTilt`), because a two-axis free orbit left the board
   * askew after every wobbly click and nothing ever put it back.
   *
   * Moves the *target* and lets `update` carry the camera there, rather than
   * writing the angles and the matrix on the spot. Applying inside a pointer
   * event meant the camera moved on input events and again on frames, at
   * whatever rate the pointer happened to fire — so the view stepped unevenly
   * under a smooth drag, and anything reading the camera from a different
   * callback saw it mid-change.
   *
   * The follow is stiff enough that it still tracks the cursor: a dragged
   * camera that lags feels broken, which is why this was immediate to begin
   * with. Now it lags by a frame's worth of easing and nothing more.
   */
  turn(yawDelta: number): void {
    this.targetYaw += yawDelta;
    // A turn is not a throw: whatever the last pan left behind is not this
    // gesture's, and a released turn snaps rather than flings.
    this.stopFling();
    this.panning = false;
    this.dragging = true;
  }

  /**
   * The pointer let go: settle the gesture rather than stopping dead.
   *
   * Two halves, and both are about the release being part of the gesture. The
   * yaw goes to the nearest eighth, so a free turn always comes to rest on a
   * facing rather than wherever the hand happened to stop. A pan that was
   * still moving keeps its speed and becomes a fling — the estimate `update`
   * has been keeping *is* the throw, so the board leaves the hand at exactly
   * the speed the hand was going, and a drag that had already stopped throws
   * nothing.
   */
  release(): void {
    this.dragging = false;
    this.panning = false;
    this.swept.set(0, 0, 0);
    this.targetYaw = Math.round(this.targetYaw / EIGHTH) * EIGHTH;
    if (this.velocity.length() < STILL) this.velocity.set(0, 0, 0);
  }

  /**
   * The gesture was taken away rather than finished: settle without the throw.
   *
   * A second finger landing turns a pan into a pinch, and the few pixels the
   * first one travelled on the way to it are not a flick — so the speed is
   * dropped and the rest of the release runs as it always does.
   */
  cancel(): void {
    this.stopFling();
    this.release();
  }

  /**
   * Turn the board an eighth, easing there.
   *
   * An eighth rather than a quarter because it is the one granularity the
   * camera has: a free turn rests on an eighth, so the button steps by the
   * same amount and the two agree about where the board may face. From the
   * nearest **eighth of the target**, not of the live yaw: the target is
   * where the board is going, so two quick presses accumulate into a quarter
   * instead of the second one cancelling most of the first while the camera
   * is still catching up with it.
   */
  rotate(steps = 1): void {
    this.targetYaw = (Math.round(this.targetYaw / EIGHTH) + steps) * EIGHTH;
  }

  /**
   * Look across the board or down onto it. Eased like any other target.
   *
   * **Global, never per side.** Which way up the board is read is a property
   * of the player rather than of whose turn it is, so a handover carries the
   * facing and the zoom and leaves this alone — see `goTo`.
   */
  setTilt(tilt: Tilt): void {
    this.targetPitch = TILT_PITCH[tilt];
  }

  /**
   * Which tilt the control should read as: the nearest preset to where the
   * pitch is heading.
   *
   * Nearest rather than exact, because `resetView` and `frameNow` set a pitch
   * of their own — a reset reads as `table`, which is what it goes to.
   */
  get tilt(): Tilt {
    const to = (tilt: Tilt): number => Math.abs(this.targetPitch - TILT_PITCH[tilt]);
    return to("top") < to("table") ? "top" : "table";
  }

  /**
   * Slide the point the camera looks at, in the board plane.
   *
   * Deltas are in world units already — the caller converts pixels, because it
   * is the renderer that knows the viewport height and therefore the scale.
   * Screen-relative: dragging right moves the board right whatever the yaw, so
   * it tracks the pointer rather than the board's own axes.
   *
   * **Glued**: the ground under the pointer stays under the pointer, which is
   * what a map does and what makes a drag read as a hand rather than as a
   * request. `update` writes the focus straight from the target while this is
   * in flight; the gain and the trailing ease that used to sit between the two
   * were the whole of what felt clunky.
   */
  pan(right: number, forward: number): void {
    // The hand grabs the frame in front of the player: a drag beginning during
    // an ease takes the view over from where it *is*, rather than snapping to
    // wherever that ease was heading.
    if (!this.panning) {
      this.stopFling();
      this.targetFocus.copy(this.focus);
      this.panning = true;
    }
    this.slide(right, forward);
    // Summed until the next update, which turns the frame's sweep into a speed.
    this.swept.x += right;
    this.swept.z += forward;
    this.dragging = true;
  }

  /**
   * Point the held pan keys, screen-relative, each component −1, 0 or 1.
   *
   * A *direction*, not a step: the caller says which keys are down and
   * `update` integrates them at `KEY_SPEED`, so the glide is one motion at a
   * known speed rather than a stack of nudges arriving at the platform's key
   * repeat rate — which differed per machine and stuttered on the first one.
   * Signed exactly as `pan` is, so the two cannot disagree about which way
   * "right" is.
   *
   * Letting go stops dead. A key is a held control rather than a throw, and a
   * view that carried on after the finger came up would overshoot whatever it
   * was being lined up on.
   */
  drive(right: number, forward: number): void {
    this.driving = { right, forward };
    // Taking the wheel ends the throw: the two would otherwise add up, and a
    // key pressed to stop a fling would speed it up instead.
    if (right || forward) this.stopFling();
  }

  /**
   * Zoom by a factor, optionally keeping a board point pinned under the cursor.
   *
   * **Toward the pointer, not toward the middle.** A centre zoom means every
   * step in is followed by a pan to put back what it pushed off screen, which
   * on a board that has grown a few times is most of the interaction. The
   * correction is exact rather than iterative: under an orthographic camera a
   * point's offset from the focus scales linearly with the view height, so
   * holding `(p - focus) / view` fixed is the whole of it.
   *
   * **Exact at the focus plane under perspective, and no worse than that.**
   * The linear relation is what "`view` units fill the frame at the focus
   * plane" buys, so a point near the middle of the screen stays pinned; a
   * point out at the near or far edge drifts by the ratio between its own
   * depth and the focus's. `PERSPECTIVE_FOV` is what bounds it, and at a long
   * lens the drift over one notch is well under a square. Not solved, unlike
   * `fitView`: a fit is a promise about every square, and this is a courtesy
   * about the one under the cursor.
   *
   * The target is what is corrected, not the live camera. A wheel notch during
   * an ease would otherwise pin the point against a focus that is still moving
   * and drift by however far the ease had left to run.
   */
  zoom(factor: number, at?: { x: number; z: number }): void {
    // Before the guard below, because putting a throw down is not zooming: a
    // wheel that is inert at the limit still means the player has taken hold.
    this.stopFling();
    const next = Math.min(this.maxView, Math.max(MIN_VIEW, this.targetView * factor));
    // Clamped away to nothing — at the limits the wheel should be inert rather
    // than dragging the board sideways for free.
    if (next === this.targetView) return;

    if (at) {
      // `at` was read off the camera as it is *drawn*, which mid-ease is not
      // where it is going. Carry it forward to the target camera first, or a
      // second notch arriving before the first has landed pins a point the
      // board has already moved past — which reads as the view creeping
      // whenever the wheel is turned faster than the ease.
      const lead = this.targetView / this.view;
      const pinX = this.targetFocus.x + (at.x - this.focus.x) * lead;
      const pinZ = this.targetFocus.z + (at.z - this.focus.z) * lead;

      const ratio = next / this.targetView;
      this.targetFocus.x = pinX - (pinX - this.targetFocus.x) * ratio;
      this.targetFocus.z = pinZ - (pinZ - this.targetFocus.z) * ratio;
    }
    this.targetView = next;
    this.clampFocus();
    // Zooming somewhere is the player saying where they want to be looking,
    // exactly as a pan is — so the director hands the board back here.
    this.anchor.copy(this.targetFocus);
  }

  /**
   * How far out the wheel may go: far enough to see the whole board and no
   * further.
   *
   * A fixed limit cannot be right for a board whose whole point is that it
   * grows. The floor keeps a little air on the classic board, where fitting
   * the eight-by-eight exactly would mean the wheel had nowhere to go at all.
   */
  private get maxView(): number {
    // Past the fit, not up to it. Stopping exactly at "the whole board is on
    // screen" means the wheel dies at the moment you can see everything, which
    // reads as the control breaking — and on the classic board, where the fit
    // is most of the screen already, it barely turns at all.
    return Math.min(MAX_VIEW, Math.max(VIEW_HEIGHT * 1.25, this.fitView() * ZOOM_OUT_SLACK));
  }

  /**
   * The view height that shows every square, with a little air around it.
   *
   * Each corner is put where the camera would actually draw it — across the
   * screen and up it — rather than measuring the board's width against the
   * viewport's. At the default eighth-turn yaw those differ by the board's
   * diagonal, which is a corner hanging off the top of the screen on a control
   * whose one job is to put the whole board on it.
   */
  /**
   * How much view height it takes to hold the whole board.
   *
   * The pitch is a parameter because two callers ask about a pitch they are
   * about to *move to* rather than the one they have — `frameNow` snaps, so
   * reading `this.pitch` would fit the board for an angle the camera is
   * leaving. The margin is a parameter for the same reason: asking "does this
   * board fit at all" wants the bare number, not the number with breathing
   * room added.
   */
  fitView(pitch = this.pitch, margin = FIT_MARGIN): number {
    const halfWidth = (this.extent.maxX - this.extent.minX) / 2;
    const halfDepth = (this.extent.maxZ - this.extent.minZ) / 2;
    const cos = Math.cos(this.yaw);
    const sin = Math.sin(this.yaw);
    const lean = Math.max(0.25, Math.sin(pitch));

    let need = 0;
    /** The furthest a corner reaches up or down the screen, in ground units. */
    let reach = 0;
    for (const dx of [-halfWidth, halfWidth]) {
      for (const dz of [-halfDepth, halfDepth]) {
        const across = Math.abs(dx * cos - dz * sin);
        const up = Math.abs(dx * sin + dz * cos);
        reach = Math.max(reach, up);
        need = Math.max(
          need,
          // **The free width, not the canvas's.** Fitting asks "how much
          // board can I actually see", which is the question `freeAspect`
          // answers — `aspect` is what the *frustum* is built from and is
          // wider than the part of the page the panel leaves. Measured at
          // 1280 with a 469px panel: the board came out 896px across a free
          // area of 811, correctly centred in it and overflowing both ends,
          // with h5 and g8 off the side. Identical with no inset, since
          // `freeAspect` is `aspect` then.
          (2 * across) / Math.max(0.1, this.freeAspect),
          2 * up * lean
        );
      }
    }

    // **The near corner is bigger than the affine answer says**, and fitting is
    // the one place in this file where that matters enough to solve rather than
    // to note. Everything above measures the board at the focus plane; under a
    // perspective camera the half of it sloping toward the viewer is magnified,
    // so a fit that trusted the plane would crop the near rank — on the one
    // control whose entire job is to put every square on screen.
    //
    // Solved rather than padded with a constant, because it is a one-step fixed
    // point and a constant would be wrong on every board but the one it was
    // measured on: the affine need gives a distance, the distance gives the
    // nearest corner's magnification, and the need times that is the answer.
    // Floored at a fifth of the distance so a board reaching past the camera
    // cannot divide by nothing.
    if (this.mode === "perspective" && need > 0) {
      const range = need / 2 / Math.tan(PERSPECTIVE_FOV / 2);
      const nearest = Math.max(range * 0.2, range - reach * Math.cos(pitch));
      need *= range / nearest;
    }
    return Math.min(MAX_VIEW, Math.max(MIN_VIEW, need * margin));
  }

  /** Pull back until the whole board is on screen, and centre it. */
  fit(): void {
    this.targetView = this.fitView();
    this.centre();
  }

  /**
   * Frame the board and be there *now*, with no motion at all.
   *
   * The one deliberate exception to "every method moves a target and
   * `update` is the only writer". That rule exists because the camera was
   * once written from eight places and the clamp and the ease took turns each
   * frame — which was the shake. This is not that: it is a single call, made
   * once, before anything has been drawn.
   *
   * It exists because a game *opens* with the camera at the origin and the
   * board somewhere else — First Light's ground is centred on (9, 9) — so the
   * first thing a new player saw was a board sliding into place from off
   * frame, a third of it hanging off the edge. There is nothing to animate
   * *from* on a fresh spawn, and animating anyway is how an opening reads as
   * the game still getting ready rather than as the game.
   */
  frameNow(
    /**
     * Where to sit, when that is not the middle of the extent.
     *
     * **A run fits its whole level and centres on the ground that exists.**
     * Unrevealed regions are not drawn — they are not `cells` yet — so
     * centring on the level's middle puts the camera over ground nobody can
     * see and pushes the board into a corner with void either side. What the
     * extent is for here is how far *back* to pull; where to look is the board.
     */
    centreOn?: { x: number; z: number }
  ): void {
    // Nothing survives a snap: a throw or a held key still in flight would
    // move the frame this exists to place exactly.
    this.stopFling();
    this.driving = { right: 0, forward: 0 };
    this.panning = false;
    // Angles first: `fit` sizes the view from the pitch the camera *has*, so
    // fitting before snapping would frame the board for an angle it is about
    // to leave.
    this.yaw = this.targetYaw;
    this.pitch = this.targetPitch;

    // **Centre always; pull back only if the board does not fit.**
    //
    // The classic eight-by-eight already fits the default view with a little
    // to spare, so zooming out for it would move the opening of every hot-seat
    // game to pay for a problem only a grown board has. The bare need rather
    // than the padded one, because this is the question "is any of it off
    // screen", not "is it comfortable".
    if (this.fitView(this.pitch, 1) > this.view) {
      this.targetView = this.fitView();
    }
    if (centreOn) {
      this.anchor.set(centreOn.x, 0, centreOn.z);
      this.targetFocus.set(centreOn.x, 0, centreOn.z);
      this.clampFocus();
    } else {
      this.centre();
    }
    this.view = this.targetView;
    this.focus.copy(this.targetFocus);
    this.anchor.copy(this.targetFocus);
    // **And write the camera**, which is the half that is easy to miss.
    // `update` only calls `apply` when something has *moved*, and snapping
    // leaves the live values already equal to their targets — so without this
    // the rig's state was correct and the camera object was never
    // repositioned. It presented as a board framed perfectly according to
    // every number and still sitting off the right of the screen.
    this.apply();
  }

  /** Every square that exists, as a world-space rectangle. */
  setBoardExtent(minX: number, maxX: number, minZ: number, maxZ: number): void {
    this.extent = { minX, maxX, minZ, maxZ };
    // Clamp the *target*, not where the camera happens to be right now, and
    // let `update` carry it in. This ran on every state change and every
    // resize, so pulling the live position back while the target stayed
    // outside the region meant the two took turns each frame — the camera was
    // literally being fought over by the clamp and the ease. Moving the target
    // makes the correction a motion like any other.
    this.clampFocus();
    // A board that just grew may have made a zoom level legal that was not
    // before; one that shrank cannot, but the clamp is the same either way.
    this.targetView = Math.min(this.maxView, Math.max(MIN_VIEW, this.targetView));
  }

  /**
   * True when panning means something — which, since `EDGE_ROOM`, is always.
   *
   * It used to ask whether there was more board than viewport, and hid the
   * slide tool when there was not. That was right while the focus could not
   * leave the board's own extent: a drag genuinely did nothing on a classic
   * eight-by-eight, and offering a control that does nothing is worse than not
   * offering it.
   *
   * The focus may now be pushed past the edge on any board, so a drag always
   * moves the view — and the control that used to be correctly
   * hidden was, from a player's side, the camera being locked on the board
   * they spend most of their time on.
   *
   * Kept as a property rather than deleted: its tests pin the pan limits, and
   * the day a board is genuinely unpannable again this is where that is
   * decided.
   */
  get canPan(): boolean {
    return true;
  }

  /**
   * Look at a point on the board, easing there. Clamped to the pan region, so
   * asking for somewhere off the edge settles as close as the board allows —
   * which is what makes "look at ground that has not appeared yet" resolve to
   * "look toward it" rather than to nothing at all.
   */
  focusOn(x: number, z: number, remember = true): void {
    if (remember) this.anchor.set(x, 0, z);
    this.targetFocus.set(x, 0, z);
    this.clampFocus();
    this.dragging = false;
    this.panning = false;
    // Somewhere else to look ends a throw: a view still gliding past the point
    // it was sent to is the board arguing with the request.
    this.stopFling();
  }

  /**
   * Is this board point on screen, within `inset` of the way out to the edge?
   *
   * The question a borrowed look should ask before taking the view: a piece
   * moving in the middle of what the player is already looking at does not
   * need the board pulled toward it, and pulling it is a shove rather than a
   * lead. `inset` of 1 is the whole viewport; less than that leaves a margin
   * around the edge that still counts as "you would rather not have to look
   * for it".
   *
   * Measured against the **live** focus, view and pitch rather than their
   * targets, because "can the player see it" is a question about the frame in
   * front of them and not about where the camera is heading.
   *
   * `toScreen` rotates a board offset onto the screen's axes and `screenHalf`
   * is already in those same units — the foreshortening that turns them into
   * pixels lives there — so the two compare directly. Doing it by projecting
   * through the camera would be the same test with a matrix in the middle of
   * it, and it would not be testable without a browser.
   *
   * **Measured at the focus plane, both projections.** Under perspective a
   * point on the near half of the board is magnified and leaves the frame a
   * little sooner than this says, so the answer errs toward "you can see it"
   * out at the edges. That is the right way for it to be wrong: the cost of a
   * false yes is a borrowed look not taken for something already half in
   * view, and the cost of a false no is the board being shoved when it did not
   * need to move.
   */
  covers(x: number, z: number, inset = 1): boolean {
    const screen = this.screenHalf;
    const offset = this.toScreen(x - this.focus.x, z - this.focus.z);
    return (
      Math.abs(offset.across) <= screen.across * inset &&
      Math.abs(offset.up) <= screen.up * inset
    );
  }

  /**
   * Back to where the player left the view.
   *
   * A no-op when the anchor is already where we are, so a director that
   * returns after every borrowed look costs nothing on a board that never
   * moved.
   */
  returnToAnchor(): void {
    this.focusOn(this.anchor.x, this.anchor.z, false);
  }

  /** Back to the middle of the *board*, without touching the angle. */
  centre(): void {
    const x = (this.extent.minX + this.extent.maxX) / 2;
    const z = (this.extent.minZ + this.extent.maxZ) / 2;
    this.anchor.set(x, 0, z);
    this.targetFocus.set(x, 0, z);
    this.clampFocus();
    this.stopFling();
  }

  /**
   * Pull back until the whole board is on screen, easing, without taking it.
   *
   * `fit` snaps the anchor as well, which makes it a *given* view — right for
   * a reset, wrong for a look the player did not ask for. New ground opening
   * is the case this exists for: panning to a region that has just appeared
   * shows the region and hides how it joins on, which is the one thing a
   * player needs to see at that moment. `returnView` still hands the board
   * back where they left it, because the anchor never moved.
   *
   * A no-op when it all fits already, so the classic board and an early run
   * never pay for it — and the bare need rather than the padded one, because
   * the question is "is any of it off screen".
   */
  frameBoardBorrowed(): void {
    const x = (this.extent.minX + this.extent.maxX) / 2;
    const z = (this.extent.minZ + this.extent.maxZ) / 2;
    this.targetFocus.set(x, 0, z);
    if (this.fitView(this.targetPitch, 1) > this.targetView) {
      this.targetView = this.fitView();
    }
    this.clampFocus();
    this.dragging = false;
    this.panning = false;
    this.stopFling();
  }

  /** Back to the default viewing angle and zoom, keeping the current facing. */
  resetView(): void {
    this.targetPitch = DEFAULT_PITCH;
    this.targetView = Math.min(this.maxView, Math.max(MIN_VIEW, VIEW_HEIGHT));
    this.clampFocus();
    this.dragging = false;
    this.panning = false;
    this.stopFling();
  }

  /**
   * Which way the board is facing on screen, in radians.
   *
   * Read by the interface, not by the scene: a control that offers a
   * *direction* has to be drawn the way that direction currently looks, or it
   * is asking about north while the player is looking at the board sideways.
   */
  get viewYaw(): number {
    return this.yaw;
  }

  /** Where the camera is right now, for storing a per-side viewpoint. */
  get pose(): CameraPose {
    return {
      yaw: this.yaw,
      pitch: this.pitch,
      focusX: this.focus.x,
      focusZ: this.focus.z,
      view: this.view
    };
  }

  /**
   * Ease to a stored viewpoint.
   *
   * The yaw target is chosen as the nearest equivalent angle to where we
   * already are, so a half-turn between two seated players goes the short way
   * round instead of unwinding three quarters of a circle because one pose
   * happens to be recorded at 2*PI and the other at 0.
   *
   * **`pose.pitch` is read and deliberately not applied.** Pitch is global —
   * table or top is how this player reads a board, not a fact about whose turn
   * it is — so a handover carries the facing, the focus and the zoom and
   * leaves the tilt where the player put it. The field stays on `CameraPose`
   * because it is also a *reading* (`frameNow`, `cameraPose`, the pan scale,
   * anything placing sound in the board's space), and dropping it would cost
   * those a getter to say the same thing.
   */
  goTo(pose: CameraPose): void {
    const turns = Math.round((this.yaw - pose.yaw) / (2 * Math.PI));
    this.targetYaw = pose.yaw + turns * 2 * Math.PI;
    // A pose recorded before the board grew can be zoomed further out than the
    // board now allows, and one written by hand carries no zoom at all.
    this.targetView = Math.min(
      this.maxView,
      Math.max(MIN_VIEW, pose.view ?? this.targetView)
    );
    this.targetFocus.set(pose.focusX ?? 0, 0, pose.focusZ ?? 0);
    this.clampFocus();
    this.panning = false;
    this.stopFling();
  }

  /**
   * True when the camera has finished easing and is holding still.
   *
   * A fling and a held key have to be asked about separately: both write the
   * focus straight from the target every frame, so the two are equal *between*
   * frames and the arithmetic below would call a board mid-glide settled.
   */
  get settled(): boolean {
    if (this.flinging || this.driving.right || this.driving.forward) return false;
    return (
      Math.abs(this.targetYaw - this.yaw) < 1e-4 &&
      Math.abs(this.targetPitch - this.pitch) < 1e-4 &&
      Math.abs(this.targetView - this.view) < 1e-4 &&
      this.targetFocus.distanceToSquared(this.focus) < 1e-8
    );
  }

  /** Which quarter-turn is showing — the UI uses it to label the near side. */
  get quarterTurns(): number {
    return ((Math.round(this.yaw / QUARTER) % 4) + 4) % 4;
  }

  /**
   * Carry the camera toward its target. Returns true while still moving.
   *
   * **The only place the camera is written.** Everything else moves the
   * target and lets this catch up, which is what makes the view a single
   * smooth thing rather than the sum of however many input events arrived
   * this frame.
   *
   * Frame-rate independent: the naive `x += (target - x) * dt * k` gives a
   * different curve at 60Hz and 144Hz, which reads as the camera being
   * "heavier" on a slow machine. The exponential form is the same motion at
   * any step size.
   */
  update(dt: number): boolean {
    const time = Math.max(0, dt);
    // The pitch and the viewport decide how much board is on screen, and both
    // move under a drag — so how far the view may travel is re-derived here
    // rather than only where it is set. Note this moves the *target* if it has
    // drifted outside; the live focus is written once, below.
    this.clampFocus();

    // The hand's speed, while the pointer is down: this frame's sweep over its
    // length, folded into a running estimate. A frame the hand did not move
    // pulls the estimate toward nothing, so only a pan still going when the
    // pointer lifts flings at all.
    if (this.panning && time > 0) {
      this.velocity.lerp(this.swept.clone().divideScalar(time), VELOCITY_SMOOTHING);
      this.swept.set(0, 0, 0);
    }

    // **Read before the throw is spent**, so the frame that ends a fling is
    // still one the focus is carried through. Taken after, the last step of
    // the glide — the one that drops the speed under `STILL` — would be handed
    // to the ease instead, which then spends half a second walking off a
    // quarter of a millimetre before anything counts as settled.
    const glued = this.glued;

    // **Keys before the fling, and never both**: `drive` already put the throw
    // down, so this is only the order the two are read in.
    if (this.driving.right || this.driving.forward) {
      const reach = KEY_SPEED * this.view * time;
      this.slide(this.driving.right * reach, this.driving.forward * reach);
    } else if (this.flinging) {
      const carried = this.slide(this.velocity.x * time, this.velocity.z * time);
      if (!carried) {
        // **The edge stops a throw.** Left running it spends the next second
        // pressed against the clamp, which reads as the board stuck rather
        // than as the fling having arrived.
        this.velocity.set(0, 0, 0);
      } else {
        this.velocity.multiplyScalar(Math.exp(-FLING_FRICTION * time));
        if (this.velocity.length() < STILL) this.velocity.set(0, 0, 0);
      }
    }

    const stiffness = this.dragging ? DRAG_FOLLOW : EASE_FOLLOW;
    const step = 1 - Math.exp(-stiffness * time);
    const zoomStep = 1 - Math.exp(-ZOOM_FOLLOW * time);

    const dYaw = this.targetYaw - this.yaw;
    const dPitch = this.targetPitch - this.pitch;
    const dView = this.targetView - this.view;
    const dFocus = this.targetFocus.distanceToSquared(this.focus);

    if (
      Math.abs(dYaw) < 1e-4 &&
      Math.abs(dPitch) < 1e-4 &&
      Math.abs(dView) < 1e-4 &&
      dFocus < 1e-8
    ) {
      if (
        this.yaw !== this.targetYaw ||
        this.pitch !== this.targetPitch ||
        this.view !== this.targetView ||
        !this.focus.equals(this.targetFocus)
      ) {
        this.yaw = this.targetYaw;
        this.pitch = this.targetPitch;
        this.view = this.targetView;
        this.focus.copy(this.targetFocus);
        this.apply();
      }
      return false;
    }

    this.yaw += dYaw * step;
    this.pitch += dPitch * step;
    this.view += dView * zoomStep;
    // **Glued, or eased.** A hand, its throw and a held key have already moved
    // the target exactly as far as they meant to, so the focus is written from
    // it; everything else is somewhere the camera is *arriving*, which is what
    // the ease is for.
    if (glued) this.focus.copy(this.targetFocus);
    else this.focus.lerp(this.targetFocus, step);
    this.apply();
    return true;
  }

  /**
   * Write the camera. Called from the constructor for the first frame, from
   * `update` thereafter, and from `frameNow` — which snaps, so there is
   * nothing left for `update` to notice. Nowhere else: every other method on
   * this class moves a *target*.
   */
  private apply(): void {
    this.applyProjection();
    // `distance` reads `view`, so this has to run whenever the zoom moves —
    // which it does, since `update` calls `apply` for any change at all. Under
    // the orthographic camera it is a constant and the position is unchanged
    // by a zoom, exactly as before.
    const range = this.distance;
    const horizontal = Math.cos(this.pitch) * range;
    const camera = this.camera;
    camera.position.set(
      this.focus.x + Math.sin(this.yaw) * horizontal,
      Math.sin(this.pitch) * range,
      this.focus.z + Math.cos(this.yaw) * horizontal
    );
    camera.lookAt(this.focus);
  }

  /**
   * How much board is on screen vertically, in world units — the pan scale.
   *
   * The *live* value, not the target: a pan that happens while a zoom is still
   * easing has to convert its pixels against the frustum currently on screen,
   * or the board slides out from under the cursor mid-gesture.
   */
  get viewHeight(): number {
    return this.view;
  }

  /** How far in the view is, as a multiple of the default. For the UI to show. */
  get zoomFactor(): number {
    return VIEW_HEIGHT / this.view;
  }
}
